import http, { httpClient } from '../utils/httpClient';
import { apiErrorMessage } from '../utils/apiError';
import {
  RoomType, CreateRoomTypeRequest, UpdateRoomTypeRequest,
  Room, CreateRoomRequest, UpdateRoomRequest, UpdateRoomStatusRequest,
  RoomRate, CreateRoomRateRequest, UpdateRoomRateRequest,
  RoomStatus, KYCStatus, KYCDocument, KYCDocumentType,
} from '../types/rooms';

const BASE = '/api/v1/hotel';

function extractErrorMessage(body: any, status: number): string {
  if (!body) return `HTTP ${status}`;
  const err = body.error;
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    if (typeof err.message === 'string' && err.message.trim()) return err.message;
    if (typeof err.code === 'string' && err.code.trim()) return err.code;
  }
  if (typeof body.message === 'string' && body.message.trim()) return body.message;
  if (typeof body.details === 'string' && body.details.trim()) return body.details;
  return `HTTP ${status}`;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(extractErrorMessage(body, response.status));
  }
  return response.json();
}

// ── Room Types ──

export async function listRoomTypes(): Promise<RoomType[]> {
  const res = await http.get(`${BASE}/room-types`);
  const data = await handleResponse<any>(res);
  // API returns {room_types: [...], count} wrapper
  return Array.isArray(data) ? data : (data.room_types || []);
}

export async function createRoomType(data: CreateRoomTypeRequest): Promise<RoomType> {
  const res = await http.post(`${BASE}/room-types`, data);
  return handleResponse<RoomType>(res);
}

export async function updateRoomType(id: string, data: UpdateRoomTypeRequest): Promise<RoomType> {
  const res = await http.put(`${BASE}/room-types/${id}`, data);
  return handleResponse<RoomType>(res);
}

export async function deleteRoomType(id: string): Promise<void> {
  const res = await http.delete(`${BASE}/room-types/${id}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(extractErrorMessage(body, res.status));
  }
}

// ── Rooms ──

export async function listRooms(filters?: { status?: RoomStatus; room_type_id?: string }): Promise<Room[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.room_type_id) params.set('room_type_id', filters.room_type_id);
  const qs = params.toString();
  const res = await http.get(`${BASE}/rooms${qs ? `?${qs}` : ''}`);
  const data = await handleResponse<any>(res);
  // API returns {rooms: [...], count} wrapper
  return Array.isArray(data) ? data : (data.rooms || []);
}

function isValidISODate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  // Guard against corrupted years like 0202 / 0002 from bad date inputs
  const year = d.getFullYear();
  return year >= 2000 && year <= 2100;
}

export async function listAvailableRooms(checkIn: string, checkOut: string): Promise<Room[]> {
  if (!isValidISODate(checkIn) || !isValidISODate(checkOut)) {
    return [];
  }
  if (checkOut <= checkIn) {
    return [];
  }
  const params = new URLSearchParams();
  params.set('check_in', checkIn);
  params.set('check_out', checkOut);
  const res = await http.get(`${BASE}/rooms/available?${params.toString()}`);
  const data = await handleResponse<any>(res);
  return Array.isArray(data) ? data : (data.rooms || []);
}

export async function createRoom(data: CreateRoomRequest): Promise<Room> {
  const res = await http.post(`${BASE}/rooms`, data);
  return handleResponse<Room>(res);
}

export async function updateRoom(id: string, data: UpdateRoomRequest): Promise<Room> {
  const res = await http.put(`${BASE}/rooms/${id}`, data);
  return handleResponse<Room>(res);
}

export async function updateRoomStatus(id: string, data: UpdateRoomStatusRequest): Promise<Room> {
  const res = await http.patch(`${BASE}/rooms/${id}/status`, data);
  return handleResponse<Room>(res);
}

export async function deleteRoom(id: string): Promise<void> {
  const res = await http.delete(`${BASE}/rooms/${id}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(extractErrorMessage(body, res.status));
  }
}

// ── Room Rates ──

export async function listRoomRates(filters?: { room_type_id?: string; active?: boolean }): Promise<RoomRate[]> {
  const params = new URLSearchParams();
  if (filters?.room_type_id) params.set('room_type_id', filters.room_type_id);
  if (filters?.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  const res = await http.get(`${BASE}/rates${qs ? `?${qs}` : ''}`);
  const data = await handleResponse<any>(res);
  // API returns {rates: [...], count} wrapper
  return Array.isArray(data) ? data : (data.rates || []);
}

export async function createRoomRate(data: CreateRoomRateRequest): Promise<RoomRate> {
  const res = await http.post(`${BASE}/rates`, data);
  return handleResponse<RoomRate>(res);
}

export async function updateRoomRate(id: string, data: UpdateRoomRateRequest): Promise<RoomRate> {
  const res = await http.put(`${BASE}/rates/${id}`, data);
  return handleResponse<RoomRate>(res);
}

export async function deleteRoomRate(id: string): Promise<void> {
  const res = await http.delete(`${BASE}/rates/${id}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(apiErrorMessage(body, `HTTP ${res.status}`));
  }
}

// ── KYC ──

export async function getKYCStatus(): Promise<KYCStatus> {
  const res = await http.get(`${BASE}/kyc`);
  return handleResponse<KYCStatus>(res);
}

export async function uploadKYCDocument(file: File, documentType: KYCDocumentType): Promise<KYCDocument> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await httpClient(`${BASE}/kyc/documents`, {
    method: 'POST',
    body: formData,
    headers,
  });
  return handleResponse<KYCDocument>(res);
}

export async function submitKYCDossier(): Promise<void> {
  const res = await http.post(`${BASE}/kyc/submit`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(apiErrorMessage(body, `HTTP ${res.status}`));
  }
}

export async function deleteKYCDocument(id: string): Promise<void> {
  const res = await http.delete(`${BASE}/kyc/documents/${id}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(apiErrorMessage(body, `HTTP ${res.status}`));
  }
}

export const roomService = {
  listRoomTypes, createRoomType, updateRoomType, deleteRoomType,
  listRooms, createRoom, updateRoom, updateRoomStatus, deleteRoom,
  listRoomRates, createRoomRate, updateRoomRate, deleteRoomRate,
  getKYCStatus, uploadKYCDocument, submitKYCDossier, deleteKYCDocument,
};

export default roomService;
